/* eslint-disable prefer-const, jsx-a11y/no-onchange */
import { h, JSX } from 'preact'
import { OppslutningTableItem } from '../Oppslutning/mapping'
import mapPath from '../../../../../assets/kommuner-forenklet-class.svg'
import { withSectionErrorBoundary } from '../../shared/SectionErrorBoundary/SectionErrorBoundary'

import { getRanges } from '../../../../lib/classify-series'

import { requestThenCache } from '../../../../lib/request'
import { useResource } from '../../../../lib/hooks/use-resource'
import { Message } from '../../../../lib/intl/Message'
import { Parti } from '../../../domain'
import { useWidgetUrls } from '../../../routes/widget'
import { TabPanel, Tabs } from '../../shared/Tabs/Tabs'
import { TabHeading } from '../../shared/Tabs/TabHeading'
import { ClassEntry, createBounds, createClasses, createClassList } from './helpers'

/* styles */
import styles from './OppslutningKart.module.css'
import { idFrom } from '../../../api'
import { useState } from 'preact/hooks'

const MAP_ID = 'oppslutning-kart-svg'

type TabsId = 'antall' | 'endring'

type Props = {
  items: OppslutningTableItem[]
  parti: Parti
  initiallyOpen?: TabsId
  widgetFooter?: JSX.Element
}

function _OppslutningKart({ items, parti, initiallyOpen, widgetFooter }: Props) {
  const map = useResource({
    clientSide: () => requestThenCache(mapPath),
    serverSide: () =>
      // __IS_SERVER__ guard needed to ensure webpack does not include the entire svg in the client side JS bundle
      __IS_SERVER__ && require('../../../../../assets/kommuner-forenklet-class.svg?inline'),
    selector: '#oppslutning-kart-svg svg',
    cacheKey: 'oppslutning-kart-svg'
  })

  // Vi kan ikke vise endring hvis endringene ikke finnes i alle items
  const hasEndring = items.some(item => item.endring !== undefined)
  const initiallyOpenTab = (hasEndring && initiallyOpen) || 'antall'

  const [activeTab, setActiveTab] = useState<TabsId>(initiallyOpenTab)
  function onChange(idx) {
    setActiveTab(idx === 1 ? 'endring' : 'antall')
  }

  const { oppslutningKartUrl } = useWidgetUrls()
  const widgetUrl = oppslutningKartUrl(parti, 'kommune', activeTab)

  return (
    <section
      class={styles.oppslutningKart}
      data-widget-url={widgetUrl}
      data-valgtype={'kommune'}
      data-grunndata={parti.id}
      data-modulnavn="oppslutning-kart"
    >
      <Message as="h3" id="hvor-bor-velgerne-tittel" values={{ partiet: parti.navn }} />
      <Tabs
        headings={[
          <TabHeading icon="tab-stemmer">
            <Message id={`oppslutning-tab-tittel-oppslutning`} />
          </TabHeading>,
          <TabHeading icon="tab-endring">
            <Message id={`oppslutning-tab-tittel-endring`} />
          </TabHeading>
        ]}
        onChange={onChange}
      >
        <TabPanel hidden={initiallyOpenTab !== 'antall'}>
          <Message as="p" id="hvor-bor-velgerne-beskrivelse" values={{ partiet: parti.navn }} />
          <Kart
            items={items}
            farge={parti.farge}
            map={map}
            scopeClass={`${parti.id}-oppslutning`}
            toValue={item => item.prosent}
          />
        </TabPanel>
        <TabPanel hidden={initiallyOpenTab !== 'endring'}>
          <Message
            as="p"
            id="hvor-bor-velgerne-beskrivelse-endring"
            values={{ partiet: parti.navn }}
          />
          <Kart
            items={items}
            farge={parti.farge}
            map={map}
            scopeClass={`${parti.id}-opp`}
            toValue={item => (typeof item.endring !== 'undefined' ? item.endring : undefined)}
          />
        </TabPanel>
      </Tabs>
      {widgetFooter}
    </section>
  )
}

function formatRanges(a: number, b: number): [number, number] {
  return [a, b] // `${a.toFixed(1)}% - ${b.toFixed(1)}%`
}

function generateStyleString(scopeClass: string, classEntries: ClassEntry[]) {
  return (
    `.${scopeClass} path {fill: #ffffff}` +
    classEntries
      .map(({ clazz, knrs }) => {
        const selector = knrs.map(id => `.${scopeClass} path.knr${id}`).join(',')
        const value = `{fill: ${clazz};stroke:black;}`
        return `${selector}${value}`
      })
      .join('')
  )
}

type KartProps = {
  scopeClass: string
  items: OppslutningTableItem[]
  farge: string
  map: string
  toValue: (OppslutningTableItem) => number | undefined
}
function Kart({ scopeClass, items, farge, map, toValue }: KartProps) {
  const bucketCount = 4
  const classes = createClasses(bucketCount, farge)

  const entries = items.map(item => [item.id, toValue(item)])
  const values = entries
    .map(([_, value]) => value)
    .filter(value => typeof value === 'number') as number[]

  const bounds = createBounds(values, bucketCount)
  const ranges = getRanges(bounds, formatRanges)
  const classList = createClassList(entries, bounds, classes)
  const style = generateStyleString(scopeClass, classList)

  const firstVisibleIdx = classList.findIndex(({ knrs }) => knrs && knrs.length > 0)
  const lastVisibleEntry = [...classList].reverse().find(({ knrs }) => knrs && knrs.length > 0)
  const lastVisibleIdx = lastVisibleEntry
    ? classList.findIndex(({ clazz }) => clazz === lastVisibleEntry.clazz)
    : undefined

  return (
    <div class={`${styles.oppslutningKartKart} ${scopeClass}`}>
      <style dangerouslySetInnerHTML={{ __html: style }} />
      <div class={styles.oppslutningKartWrapper}>
        <div id={MAP_ID} dangerouslySetInnerHTML={{ __html: map || '' }} />
        {bounds && (
          <div class={styles.legends}>
            {classList
              .slice(firstVisibleIdx, lastVisibleIdx + 1)
              .reverse()
              .map(({ clazz, idx }) => {
                const range = ranges[idx]
                return (
                  <div class={styles.legend}>
                    <span class={styles.box} style={{ background: clazz }} />
                    <Message
                      type="span"
                      id="hvor-bor-velgerne-legend"
                      values={{ fra: range[0], til: range[1] }}
                    />
                  </div>
                )
              })}
          </div>
        )}
      </div>
    </div>
  )
}

export const OppslutningKart = withSectionErrorBoundary(_OppslutningKart)
